import {useSyncExternalStore} from "react";
import {useRootDispatch, useRootSelector} from "./useOutlawStore";
import {InterestMethods, LeadPeriodChoices, RoundingMethods} from "../Configuration/GeneralOptions/OptionConstants";
import Option from "../entities/option";
import util from "../lib/util";
import {getCurrentFirmId, getCurrentUserId} from "../lib/picos/currentLogin";
import {genOptionUUID} from "../lib/picos/uuids";
import * as OptionActions from "../store/actions/optionActions";
import {deleteOption as deleteOptionDep} from "../store/deps/optionDeps";

// Constants for option names
export enum OptionNames {
    // Outlaw Practice
    BILLING_INCREMENT = 'billingIncrement',
    EMAIL_CLIENT = 'paymentEmailClient',
    EMAIL_PAYER = 'paymentEmailPayer',
    EMAIL_SELF = 'paymentEmailSelf',
    FONT_FAMILY = 'invoiceFontFamily',
    FONT_SIZE = 'invoiceFontSize',
    FIRM_ADDRESS = 'invoiceFirmAddress',
    FIRM_LETTERHEAD = 'invoiceFirmLetterhead',
    LETTERHEAD_WIDTH = 'invoiceLetterheadWidth',
    PAID_CONSULTS = 'paidConsults',
    PAID_CONSULT_PRICE = 'paidConsultPrice',
    PAID_CONSULT_DURATION = 'paidConsultDuration',
    PAID_CONSULT_NAME = 'paidConsultName',
    PAID_CONSULT_ACTIVITY = 'paidConsultActivityId',
    LEAD_FOLLOWUP_ACTIVITY = 'leadFollowupActivityId',
    LEAD_INITIAL_CONSULT_USER = 'leadInitialConsultUser',
    USE_CASE_TRUST = 'useCaseTrust',
    INVOICE_DEFAULT_SUMMARY = 'invoiceDefaultSummary',
    INVOICE_NARRATIVE_HEADER = 'invoiceNarrativeHeader',
    INVOICE_TERMS = 'invoiceTerms',
    ENFORCE_ACTIVITY_TYPE = 'enforceActivityType',
    ENFORCE_CATEGORY_SELECTED = 'enforceCategorySelected',
    ENFORCE_TIE_INS_COMPLETE = 'enforceTieInsComplete',
    ROUNDING_METHOD = 'roundingMethod',
    SHOW_HELP = 'showHelp',
    SOUND_FOR_WIN = 'soundForWin',
    SOUND_FOR_TASK_COMPLETE = 'soundForTaskComplete',
    TASK_EMOJIS = 'taskEmojis',

    // Tax & Interest
    INVOICE_TAX_RATE = 'invoiceTaxRate',
    INVOICE_TAX_LABEL = 'invoiceTaxLabel',
    INVOICE_INTEREST_RATE = 'invoiceInterestRate',
    INVOICE_INTEREST_METHOD = 'invoiceInterestMethod',
    INVOICE_INTEREST_GRACE_PERIOD = 'invoiceInterestGracePeriod',
    INVOICE_LEAD_PERIOD = 'invoiceLeadPeriod',
    // A JSON array of expected-expense rules (OWA-985); empty means none
    INVOICE_EXPECTED_EXPENSES = 'invoiceExpectedExpenses',

    // Reporting
    PAYMENT_ALLOCATION_METHOD = 'paymentAllocationMethod',
    UTILIZATION_HOLIDAYS_PER_YEAR = 'utilizationHolidaysPerYear',
    UTILIZATION_VACATION_DAYS = 'utilizationVacationDays',

    // Session Replay
    SESSION_REPLAY_OPT_OUT = 'sessionReplayOptOut',

    // Outlaw Payments
    PAYMENT_PORTAL = 'paymentPortal',
    PAYMENT_PORTAL_BENEFACTORS_LINK = 'paymentPortalBenefactorsLink',
    PAYMENT_PORTAL_BENEFACTORS_INVOICE = 'paymentPortalBenefactorsInvoice',
}

export enum TypeValues {
    Boolean = 'boolean',
    Numeric = 'numeric',
    String ='string',
}

export type OptionTypes = string | number | boolean;

export type TypeValueMap<T extends OptionTypes> =
    T extends boolean ? TypeValues.Boolean :
    T extends number ? TypeValues.Numeric :
    TypeValues.String;

export type Defaults<T extends OptionTypes> = {
    defaultValue: T,
    type: TypeValueMap<T>,
}

// A queued option reaches Redux only after its IndexedDB write (optionSagas.saveOption). Until the
// saga reports that write, getOption returns the newest queued option for its firm and label, so a
// second save compares against it and reuses its optionId (OWA-913, OWA-914).
const pendingOptions = new Map<string, Option>();
const pendingKey = (label: string) => `${getCurrentFirmId()}:${label}`;

// Callers read pending options through getOption, so they re-render whenever one is queued or
// reports; a page can then show an option's value straight from here (OWA-917)
let pendingVersion = 0;
const pendingListeners = new Set<() => void>();
const pendingChanged = () => {
    pendingVersion++;
    pendingListeners.forEach(listener => listener());
};
const subscribePending = (listener: () => void) => {
    pendingListeners.add(listener);
    return () => { pendingListeners.delete(listener); };
};
const pendingSnapshot = () => pendingVersion;

// Every queued option write that has not reported yet. OptionHelper reads Redux only, so
// finalization and invoice PDFs wait for these before reading an option (OWA-917).
const pendingWrites = new Set<Promise<boolean>>();

/**
 * Resolves once every option write queued so far has reported, so Redux holds each option's
 * settled value.
 */
export const optionWritesSettled = async (): Promise<void> => {
    await Promise.all(pendingWrites);
};

function useOptions() {
    const options = useRootSelector(state => state.options);
    useSyncExternalStore(subscribePending, pendingSnapshot);
    const dispatch = useRootDispatch();

    const getDefaults = <T extends OptionTypes>(label: OptionNames): Defaults<T> => {
        switch (label) {
            case OptionNames.BILLING_INCREMENT:
                return {defaultValue: 1, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.EMAIL_CLIENT:
            case OptionNames.EMAIL_PAYER:
            case OptionNames.EMAIL_SELF:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL_BENEFACTORS_LINK:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.FONT_FAMILY:
                return {defaultValue: 'Open Sans, sans-serif', type: TypeValues.String} as Defaults<T>;
            case OptionNames.FONT_SIZE:
                return {defaultValue: '10pt', type: TypeValues.String} as Defaults<T>;
            case OptionNames.FIRM_ADDRESS:
                return {defaultValue: null, type: TypeValues.String} as Defaults<T>;
            case OptionNames.FIRM_LETTERHEAD:
                return {defaultValue: 'Set up letterhead in Options/Invoicing', type: TypeValues.String} as Defaults<T>;
            case OptionNames.LETTERHEAD_WIDTH:
                // No default: until a firm sets a width, the image keeps its own size.
                return {defaultValue: null, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.PAID_CONSULTS:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAID_CONSULT_PRICE:
                return {defaultValue: 250, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.PAID_CONSULT_DURATION:
                return {defaultValue: 1, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.PAID_CONSULT_NAME:
                return {defaultValue: 'Initial Consultation', type: TypeValues.String} as Defaults<T>;
            case OptionNames.PAID_CONSULT_ACTIVITY:
                return {defaultValue: 'aea93c81-c896-4116-82f0-99864deef232', type: TypeValues.String} as Defaults<T>;
            case OptionNames.LEAD_FOLLOWUP_ACTIVITY:
                return {defaultValue: '7a87b303-ce41-428e-98b2-bfdca74d2a83', type: TypeValues.String} as Defaults<T>;
            case OptionNames.LEAD_INITIAL_CONSULT_USER:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.USE_CASE_TRUST:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.INVOICE_DEFAULT_SUMMARY:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_NARRATIVE_HEADER:
                return {defaultValue: 'Work We Did for You', type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_TERMS:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.ENFORCE_ACTIVITY_TYPE:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.ENFORCE_CATEGORY_SELECTED:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.ENFORCE_TIE_INS_COMPLETE:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.TASK_EMOJIS:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.ROUNDING_METHOD:
                return {defaultValue: RoundingMethods.ROUNDING_METHOD_MIDDLE, type: TypeValues.String} as Defaults<T>;
            case OptionNames.SHOW_HELP:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.SOUND_FOR_TASK_COMPLETE:
            case OptionNames.SOUND_FOR_WIN:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_ALLOCATION_METHOD:
                return {defaultValue: 'proportional', type: TypeValues.String} as Defaults<T>;
            case OptionNames.UTILIZATION_HOLIDAYS_PER_YEAR:
                return {defaultValue: 11, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.UTILIZATION_VACATION_DAYS:
                return {defaultValue: 15, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.SESSION_REPLAY_OPT_OUT:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL:
                return {defaultValue: true, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.PAYMENT_PORTAL_BENEFACTORS_INVOICE:
                return {defaultValue: false, type: TypeValues.Boolean} as Defaults<T>;
            case OptionNames.INVOICE_TAX_RATE:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.INVOICE_TAX_LABEL:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_INTEREST_RATE:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.INVOICE_INTEREST_METHOD:
                return {defaultValue: InterestMethods.SIMPLE_MONTHLY, type: TypeValues.String} as Defaults<T>;
            case OptionNames.INVOICE_INTEREST_GRACE_PERIOD:
                return {defaultValue: 0, type: TypeValues.Numeric} as Defaults<T>;
            case OptionNames.INVOICE_LEAD_PERIOD:
                return {defaultValue: LeadPeriodChoices.UP_TO_CONSULT, type: TypeValues.String} as Defaults<T>;
            default:
                return {defaultValue: '', type: TypeValues.String} as Defaults<T>;
        }
    }

    /**
     * Get an option based on its label.
     *
     * @param {string} label
     * @return {Option}
     */
    const getOption = (label: OptionNames): Option => {
        // Get defaults for this label
        const {defaultValue, type} = getDefaults(label);

        const pending = pendingOptions.get(pendingKey(label));
        if (pending) return pending;

        // See if the option already exists
        const result = util.sortEntityArrayByDate(options
            .getMappingFor('label', label), 'addedOn', 'desc')[0];

        if (!result) {
            // See if it exists in defaultEntries section
            return Option.getDefaultEntries().find(o => o.label === label) || new Option({
                optionId: genOptionUUID(getCurrentFirmId(), label),
                firmId: getCurrentFirmId(),
                addedBy: getCurrentUserId(),
                addedOn: 0.1,
                status: 'C',
                userScope: false,
                label,
                type,
                value: (defaultValue != null)
                       ? type === 'string'
                         ? defaultValue
                         : type === 'boolean'
                           ? defaultValue === true || defaultValue === 'true' || defaultValue === 't'
                           : `${defaultValue}`
                       : null,

            });
        }
        else return result;
    }

    /**
     * Get the value of the option.
     *
     * @param {string} label
     * @return {string|number|boolean|null}
     */
    const getValue = <T extends OptionTypes>(label: OptionNames): T => {
        try {
            const option = getOption(label);
            const value = option.value;

            return (option.type === 'numeric'
                    ? Number.parseFloat(value)
                    : option.type === 'boolean'
                      ? typeof value === 'string'
                        ? value.toLowerCase().startsWith('t')
                        : value
                      : value) as T;
        }
        catch (err: any) {
            console.error(`useOptions: getValue(${label})`, err.message);
            return null;
        }
    }

    /**
     * Set an option by label/value and optional type.
     * @param {string} label
     * @param {string|number|boolean} value
     * @param {string} [type]
     * @returns {Option}
     */
    const setOption = (label: OptionNames, value: OptionTypes, type: TypeValues = TypeValues.String): Option => {
        // First see if the option exists already
        let option: Option;

        try {
            option = getOption(label);
        }
        catch (err) {
            // Safe to ignore
        }

        if (!option) option = new Option({
            firmId: getCurrentFirmId(),
            optionId: genOptionUUID(getCurrentFirmId(), label),
            addedBy: getCurrentUserId(),
            addedOn: util.newHRTimestamp(),
            type, label, value
        });
        else {
            // getOption may return a queued option; never change one that is being written
            option = option.clone();
            option.addedBy = getCurrentUserId();
            option.addedOn = util.newHRTimestamp();
            option.value = `${value}`;
        }

        // Now save it
        saveOptionQueue(option);

        return option;
    }

    /**
     * Queue an option save, holding the option as pending until its IndexedDB write reports.
     *
     * @param {Option} option
     * @returns {Promise<boolean>} true only when this write landed and is still the newest save of
     *     its option, so the stored value is now this option's value
     */
    const saveOptionQueue = (option: Option): Promise<boolean> => {
        const key = pendingKey(option.label);

        // A plain save: the server appends a version even after a delete, and the latest wins
        // (OWA-1294), so a never-saved option's fixed ID needs no restore. A clone of a queued
        // restore carries its flag, so clear it.
        option.restore = false;
        pendingOptions.set(key, option);
        pendingChanged();
        const written = new Promise<boolean>(resolve => dispatch(OptionActions.saveOptionQueue(option, saved => {
            const newest = pendingOptions.get(key) === option;
            if (newest) {
                pendingOptions.delete(key);
                pendingChanged();
            }
            resolve(saved && newest);
        })));
        pendingWrites.add(written);
        void written.then(() => pendingWrites.delete(written));
        return written;
    }

    /**
     * Delete an option in its entirety.
     *
     * @param {string} label
     */
    const deleteOption = (label: OptionNames) => {
        const option = getOption(label);

        // The delete helper changes the option it is given, which may be a queued save, and a later
        // save must start from the store rather than from that save
        if (pendingOptions.delete(pendingKey(label))) pendingChanged();
        if (option) deleteOptionDep(option.clone());
    }

    return {getDefaults, getValue, getOption, setOption, saveOptionQueue, deleteOption };
}

export default useOptions;